import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Box, Button, Chip } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { sendApiRequest } from 'lib/api/public';
import notify from 'lib/notify';
import { formatUserDateTime } from 'components/utils';

export interface AutoSaveConfig<TData = unknown> {
  enabled: boolean;
  type: string;
  data: TData;
  onGetDraft: (draft: TData) => void;
}

export interface AutoSaveStatus {
  status: 'idle' | 'saving' | 'saved' | 'error';
  isAutoSaving: boolean;
  lastAutoSave: Date | null;
  triggerAutoSave: () => Promise<void>;
  deleteDraft: (type: string) => Promise<void>;
  updateLastSavedData: (savedData?: unknown) => void;
  resetStatus: () => void;
}

export interface AutoSaveStatusIndicatorProps {
  status: 'idle' | 'saving' | 'saved' | 'error';
  lastAutoSave: Date | null;
  className?: string;
  type: string;
  onDiscard?: () => void;
}

const deleteDraft = async (type: string) => {
  try {
    await sendApiRequest({
      path: '/users/delete-draft',
      method: 'POST',
      data: {
        type,
      },
    });
  } catch (error) {
    notify(error);
  }
};

export const AutoSaveStatusIndicator: React.FC<AutoSaveStatusIndicatorProps> = ({
  status,
  lastAutoSave,
  className,
  type,
  onDiscard,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const getStatusColor = () => {
    switch (status) {
      case 'saving':
        return 'info';
      case 'saved':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'saving':
        return 'Auto-saving...';
      case 'saved':
        return 'Auto-saved';
      case 'error':
        return 'Auto-save failed';
      default:
        return lastAutoSave ? `Last saved: ${formatUserDateTime(lastAutoSave)}` : '';
    }
  };

  const handleDiscard = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await deleteDraft(type);
      onDiscard?.();
    } catch (error) {
      console.error('Failed to discard draft:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const statusText = getStatusText();

  if (!statusText) return null;

  const showDiscardButton = !!onDiscard && (status === 'saved' || (status === 'idle' && lastAutoSave)) && !isDeleting;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
      className={className}
    >
      <Chip
        icon={<SaveIcon fontSize="small" />}
        label={statusText}
        color={getStatusColor()}
        variant={status === 'idle' ? 'outlined' : 'filled'}
        size="small"
      />
      {showDiscardButton && (
        <Button
          variant="text"
          color="inherit"
          onClick={handleDiscard}
          size="small"
          sx={{
            minWidth: 'auto',
            px: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          Clear form
        </Button>
      )}
    </Box>
  );
};

export const useAutoSave = <TData = unknown>(config: AutoSaveConfig<TData>): AutoSaveStatus => {
  const { data, type, onGetDraft, enabled } = config;

  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const minInterval = 1000;
  const delayFrames = 180; // ~3 seconds at 60fps

  // Auto-save related refs
  const lastSavedDataRef = useRef<string>('');
  const lastSaveTimeRef = useRef<number>(0);
  const hasInitializedRef = useRef(false);
  const frameCountRef = useRef<number>(0);
  const animationFrameRef = useRef<number | undefined>(undefined);

  const resetStatus = () => {
    setStatus('idle');
    setIsAutoSaving(false);
    setLastAutoSave(null);
  };

  // Helper function to check if data is empty
  const isEmpty = (data: unknown): boolean => {
    if (data === null || data === undefined) return true;
    if (typeof data === 'string') {
      const trimmedData = data.trim();

      return trimmedData === '' || trimmedData === '{}' || trimmedData === '<br>';
    }
    if (typeof data === 'number' || typeof data === 'boolean') return false;
    if (typeof data === 'object') {
      if (Array.isArray(data)) {
        return data.length === 0 || data.every(item => isEmpty(item));
      }
      const obj = data as Record<string, unknown>;
      const keys = Object.keys(obj);
      return keys.length === 0 || keys.every(key => isEmpty(obj[key]));
    }
    return false;
  };

  const saveDraft = async (data: TData) => {
    try {
      await sendApiRequest({
        path: '/users/save-draft',
        method: 'POST',
        data: {
          type,
          data,
        },
      });
    } catch (error) {
      notify(error);
    }
  };

  const getDraft = async (type: string) => {
    try {
      const response = await sendApiRequest<TData>({
        path: `/users/get-draft?type=${type}`,
        method: 'GET',
      });

      onGetDraft(response);
    } catch (error) {
      notify(error);
    }
  };

  // Auto-save function
  const performAutoSave = useCallback(async () => {
    if (isAutoSaving || !enabled) return;

    // Don't save if data is empty
    if (isEmpty(data)) {
      await deleteDraft(type);
      return;
    }

    const currentData = JSON.stringify(data);
    const now = Date.now();

    // Don't save if data hasn't changed
    if (currentData === lastSavedDataRef.current) {
      return;
    }

    // Respect minimum interval between saves
    if (now - lastSaveTimeRef.current < minInterval) {
      return;
    }

    setIsAutoSaving(true);
    setStatus('saving');

    try {
      await saveDraft(data);

      lastSavedDataRef.current = currentData;
      lastSaveTimeRef.current = now;
      setLastAutoSave(new Date());
      setStatus('saved');

      // Reset status to idle after 2 seconds
      setTimeout(() => setStatus('idle'), 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setStatus('error');

      // Reset status to idle after 3 seconds
      setTimeout(() => setStatus('idle'), 3000);
    } finally {
      setIsAutoSaving(false);
    }
  }, [data, isAutoSaving, minInterval, enabled, type]);

  // Detect data changes using memoized serialization
  const dataString = useMemo(() => JSON.stringify(data), [data]);

  // Auto-save with delay when data changes (no setTimeout)
  useEffect(() => {
    if (!enabled || !hasInitializedRef.current) return;

    // Only save if the serialized data has changed from what we last saved
    if (dataString !== lastSavedDataRef.current) {
      // Cancel any existing delay
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      frameCountRef.current = 0;

      // Create delay using requestAnimationFrame frame counting
      const frameCounter = () => {
        frameCountRef.current++;

        if (frameCountRef.current >= delayFrames) {
          void performAutoSave();
        } else {
          animationFrameRef.current = requestAnimationFrame(frameCounter);
        }
      };

      animationFrameRef.current = requestAnimationFrame(frameCounter);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [dataString, enabled, performAutoSave, delayFrames]);

  // Initialize
  useEffect(() => {
    if (enabled && !hasInitializedRef.current) {
      lastSavedDataRef.current = JSON.stringify(data);
      hasInitializedRef.current = true;
      void getDraft(type);
    } else if (!enabled) {
      hasInitializedRef.current = false;
      setStatus('idle');
      setIsAutoSaving(false);
    }
  }, [enabled, type, data]);

  // Manual trigger function
  const triggerAutoSave = useCallback(async () => {
    if (!enabled) return;
    await performAutoSave();
  }, [performAutoSave, enabled]);

  // Update last saved data when manual save occurs
  const updateLastSavedData = useCallback(
    (savedData?: unknown) => {
      if (!enabled) return;

      const dataToSave = savedData || data;
      lastSavedDataRef.current = JSON.stringify(dataToSave);
      lastSaveTimeRef.current = Date.now();
      setLastAutoSave(new Date());
      setStatus('saved');
      setTimeout(() => setStatus('idle'), 2000);
    },
    [data, enabled],
  );

  return {
    status,
    isAutoSaving,
    lastAutoSave,
    resetStatus,
    triggerAutoSave,
    deleteDraft,
    updateLastSavedData,
  };
};

export type SimpleFieldAutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export type AutosaveAttachment = {
  name?: string;
  url?: string;
  date?: Date | string;
};

export const normalizeAttachmentsForAutosave = (attachments?: AutosaveAttachment[] | null) =>
  (attachments || [])
    .map(attachment => ({
      name: attachment.name || '',
      url: attachment.url || '',
      date: attachment.date ? new Date(attachment.date).toISOString() : '',
    }))
    .sort((left, right) => left.url.localeCompare(right.url));

export const getChangedSnapshotKeys = (
  currentData: Record<string, unknown>,
  prevData: Record<string, unknown>,
) =>
  Object.keys(currentData).filter(
    key => JSON.stringify(currentData[key]) !== JSON.stringify(prevData[key]),
  );

export interface UseSimpleFieldAutosaveConfig {
  enabled: boolean;
  isReady: boolean;
  snapshot: string;
  seedSnapshot: () => string;
  debounceMs?: number;
  isBlocked?: () => boolean;
  canSave?: (changedKeys: string[], currentData: Record<string, unknown>) => boolean;
  validate?: (changedKeys: string[], currentData: Record<string, unknown>) => Promise<boolean>;
  saveChangedFields: (changedKeys: string[], currentData: Record<string, unknown>) => Promise<void>;
  onError?: (error: unknown) => void;
}

export interface SimpleFieldAutosaveResult {
  status: SimpleFieldAutosaveStatus;
  lastAutoSave: Date | null;
  resetStatus: () => void;
}

export const useSimpleFieldAutosave = (config: UseSimpleFieldAutosaveConfig): SimpleFieldAutosaveResult => {
  const {
    enabled,
    isReady,
    snapshot,
    seedSnapshot,
    debounceMs = 1500,
    isBlocked,
    canSave,
    validate,
    saveChangedFields,
    onError,
  } = config;

  const [status, setStatus] = useState<SimpleFieldAutosaveStatus>('idle');
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);

  const lastSavedDataRef = useRef('');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSavingRef = useRef(false);
  const hasSeededRef = useRef(false);

  const resetStatus = useCallback(() => {
    setStatus('idle');
    setLastAutoSave(null);
  }, []);

  useEffect(() => {
    if (!enabled || !isReady) {
      hasSeededRef.current = false;
      return;
    }

    if (!hasSeededRef.current) {
      lastSavedDataRef.current = seedSnapshot();
      hasSeededRef.current = true;
    }
  }, [enabled, isReady, seedSnapshot]);

  useEffect(() => {
    if (!enabled || !isReady) {
      return;
    }

    if (snapshot === lastSavedDataRef.current) {
      return;
    }

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(async function saveNow() {
      if (isSavingRef.current || isBlocked?.()) {
        saveTimerRef.current = setTimeout(saveNow, 500);
        return;
      }

      const currentData = JSON.parse(snapshot) as Record<string, unknown>;
      const prevData = JSON.parse(lastSavedDataRef.current || '{}') as Record<string, unknown>;
      const changedKeys = getChangedSnapshotKeys(currentData, prevData);

      if (changedKeys.length === 0) {
        return;
      }

      if (canSave && !canSave(changedKeys, currentData)) {
        return;
      }

      if (validate) {
        const isValid = await validate(changedKeys, currentData);
        if (!isValid) {
          return;
        }
      }

      isSavingRef.current = true;
      setStatus('saving');

      try {
        await saveChangedFields(changedKeys, currentData);

        lastSavedDataRef.current = snapshot;
        setLastAutoSave(new Date());
        setStatus('saved');
        setTimeout(() => setStatus('idle'), 3000);
      } catch (error) {
        if (onError) {
          onError(error);
        } else {
          notify(error);
        }
        setStatus('error');
        setTimeout(() => setStatus('idle'), 3000);
      } finally {
        isSavingRef.current = false;
      }
    }, debounceMs);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [enabled, isReady, snapshot, debounceMs, isBlocked, canSave, validate, saveChangedFields, onError]);

  return {
    status,
    lastAutoSave,
    resetStatus,
  };
};

export interface UseEntitySimpleFieldAutosaveConfig {
  enabled: boolean;
  isReady: boolean;
  snapshot: string;
  seedSnapshot: () => string;
  isBlocked?: () => boolean;
  getValidationFields: (changedKeys: string[], currentData: Record<string, unknown>) => string[];
  // Compatible with react-hook-form UseFormTrigger (Path generics vs string[])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trigger: (names: any) => Promise<boolean>;
  updatePath: string;
  idField: string;
  entityId?: string;
}

export const useEntitySimpleFieldAutosave = (
  config: UseEntitySimpleFieldAutosaveConfig,
): SimpleFieldAutosaveResult => {
  const {
    enabled,
    isReady,
    snapshot,
    seedSnapshot,
    isBlocked,
    getValidationFields,
    trigger,
    updatePath,
    idField,
    entityId,
  } = config;

  const validate = useCallback(
    async (changedKeys: string[], currentData: Record<string, unknown>) => {
      const validationFields = getValidationFields(changedKeys, currentData);

      if (validationFields.length === 0) {
        return true;
      }

      return trigger(validationFields);
    },
    [getValidationFields, trigger],
  );

  const saveChangedFields = useCallback(
    async (changedKeys: string[], currentData: Record<string, unknown>) => {
      for (const fieldName of changedKeys) {
        await sendApiRequest({
          path: updatePath,
          method: 'POST',
          data: {
            [idField]: entityId,
            fieldName,
            value: currentData[fieldName],
          },
        });
      }
    },
    [updatePath, idField, entityId],
  );

  return useSimpleFieldAutosave({
    enabled,
    isReady,
    snapshot,
    seedSnapshot,
    isBlocked,
    validate,
    saveChangedFields,
  });
};

export const useDraft = (config: { type: string }) => {
  const { type } = config;

  const [draft, setDraft] = useState<unknown>(null);

  const getDraft = async (type: string) => {
    try {
      const response = (await sendApiRequest({
        path: `/users/get-draft?type=${type}`,
        method: 'GET',
      })) as unknown;

      setDraft(response);
    } catch (error) {
      notify(error);
    }
  };

  const renderIndicator = (onClick: () => void) => {
    if (!draft) return null;

    return (
      <Button variant="contained" color="inherit" onClick={onClick}>
        Draft
      </Button>
    );
  };

  useEffect(() => {
    void getDraft(type);
  }, [type]);

  return {
    draft,
    getDraft: () => getDraft(type),
    renderIndicator,
  };
};
